/**
 * The Documents tab: the filing list, each row a way into the viewer.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import type { DocumentListItem } from '@server/sql/documents.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, date, percent0 } from '~/i18n/format.ts'
import { Badge, Bar, Skeleton, bandOf } from '~/components/ui/primitives.tsx'
import { ErrorState, EmptyState } from '~/components/ui/empty.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Untranslated } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'

type Response = { documents: DocumentListItem[]; total: number }

export function CompanyDocuments({ symbol }: { symbol: string }) {
  const { t, locale, v } = useT()
  const superadmin = useSuperadmin()
  const { data, error, refetch } = useQuery({
    queryKey: keys.companyDocuments(symbol, ''),
    queryFn: () => api<Response>(`/api/companies/${encodeURIComponent(symbol)}/documents`),
  })

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-72 w-full" />
  if (data.documents.length === 0) return <EmptyState title={t('empty.generic')} />

  return (
    <Table>
      <thead>
        <Tr>
          <Th>{t('docs.type')}</Th>
          <Th>{t('docs.period')}</Th>
          <Th numeric>{t('docs.year')}</Th>
          <Th>{t('docs.periodEnd')}</Th>
          <Th>{t('docs.lang')}</Th>
          <Th numeric>{t('docs.pages')}</Th>
          <Th numeric>{t('docs.facts')}</Th>
          <Th>{t('docs.status')}</Th>
          {superadmin && <Th numeric>{t('docs.passRate')}</Th>}
          <Th numeric>{t('docs.review')}</Th>
          <Th numeric>{t('docs.xbrl')}</Th>
        </Tr>
      </thead>
      <tbody>
        {data.documents.map((d) => {
          const band = bandOf(d.quality.passRate)
          return (
            <Tr key={d.id} interactive>
              <Td>
                <Link
                  href={`/c/${symbol}/d/${d.id}`}
                  className="font-medium link-inline"
                >
                  {v('docType', d.docType) ?? <Untranslated>{d.docType}</Untranslated>}
                </Link>
              </Td>
              <Td>{d.period ? (v('period', d.period) ?? d.period) : '—'}</Td>
              <Td numeric>{d.fiscalYear ?? '—'}</Td>
              <Td className="text-[var(--text-muted)] whitespace-nowrap">
                {date(d.periodEnd, locale)}
              </Td>
              <Td className="text-[var(--text-muted)]">
                {d.locale ? (v('locale', d.locale) ?? d.locale) : '—'}
              </Td>
              <Td numeric className="text-[var(--text-muted)]">{d.pageCount ?? '—'}</Td>
              <Td numeric className="text-[var(--text-muted)]">{count(d.facts, locale)}</Td>
              <Td>
                <Badge state={d.status === 'verified' ? 'pass' : 'warn'}>
                  {v('docStatus', d.status) ?? d.status}
                </Badge>
              </Td>
              {/* Header and cell are gated together, or the columns shear. */}
              {superadmin && <Td numeric>
                <span className="inline-flex items-center gap-1.5">
                  <Bar value={d.quality.passRate} cells={6} />
                  <span
                    className={cn(
                      band === 'pass' ? 'text-[var(--pass)]'
                        : band === 'warn' ? 'text-[var(--warn)]'
                        : band === 'fail' ? 'text-[var(--fail)]'
                        : 'text-[var(--text-faint)]',
                    )}
                    title={t('quality.passRateOf', {
                      pct: percent0(d.quality.passRate, locale),
                      passed: count(d.quality.passed, locale),
                      total: count(d.quality.checks, locale),
                    })}
                  >
                    {percent0(d.quality.passRate, locale)}
                  </span>
                </span>
              </Td>}
              <Td numeric className="text-[var(--text-muted)]">
                {d.quality.openReview || '—'}
              </Td>
              <Td numeric>
                {/* No XBRL sibling is a different fact from 0% agreement, and
                    the two must not render the same. */}
                {d.quality.xbrlAgreement === null ? (
                  <span className="text-[var(--text-faint)]" title={t('quality.xbrlNone')}>—</span>
                ) : (
                  percent0(d.quality.xbrlAgreement, locale)
                )}
              </Td>
            </Tr>
          )
        })}
      </tbody>
    </Table>
  )
}
